// Public community template detail (D-84). Start creates only a checklist (D-72).
// Copy reuses lists the caller already has (D-73).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorBanner } from "../components/AuthShell.js";
import { StartChecklistModal } from "../components/StartChecklistModal.js";
import { AnchorButton, Button } from "../components/ui/Button.js";
import { showToast } from "../components/ui/Toast.js";
import { useAuth } from "../context/AuthContext.js";
import {
  ApiError,
  copyCommunityTemplate,
  fetchCommunityTemplate,
  startCommunityTemplate,
  type ApiCommunityTemplateDetail,
} from "../lib/api.js";
import {
  CommunityTemplateDetailView,
  communityPairButtonClass,
  type CommunityTemplateDetail,
} from "./CommunityViews.js";
import { communityTemplateTitle } from "../lib/seo.js";
import { useSignupMode } from "../hooks/useSignupMode.js";
import { signupLabel } from "../lib/signupCopy.js";

function toDetail(template: ApiCommunityTemplateDetail): CommunityTemplateDetail {
  return {
    id: template.id,
    name: template.name,
    description: template.description,
    ownerScreenName: template.ownerScreenName,
    official: template.official === true,
    copyCount: template.copyCount,
    sections: template.sections.map((section) => {
      if (section.type === "inline") {
        return {
          type: "inline",
          name: section.name,
          items: section.items.map((item) => ({ id: item.id, text: item.text })),
        };
      }
      return {
        type: "list",
        listId: section.listId,
        name: section.name,
        items: section.items.map((item) => ({ id: item.id, text: item.text })),
        subgroups: section.subgroups.map((group) => ({
          id: group.id,
          name: group.name,
          items: group.items.map((item) => ({ id: item.id, text: item.text })),
        })),
      };
    }),
  };
}

export function CommunityTemplatePage() {
  const { mode: signupMode } = useSignupMode();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAnonymous = !user;
  const { id } = useParams<{ id: string }>();

  const [detail, setDetail] = useState<ApiCommunityTemplateDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [startOpen, setStartOpen] = useState(false);

  useEffect(() => {
    if (detail) document.title = communityTemplateTitle(detail);
  }, [detail]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const template = await fetchCommunityTemplate(id);
        if (!cancelled) setDetail(template);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Failed to load template");
        setDetail(null);
        navigate("/community", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  async function onCopy() {
    if (!detail) return;
    setCopyBusy(true);
    setError(null);
    try {
      const result = await copyCommunityTemplate(detail.id);
      showToast(
        result.warnings.length > 0
          ? `Copied "${result.template.name}" to My Templates. Some lists were unavailable and were skipped.`
          : `Copied "${result.template.name}" to My Templates`
      );
      navigate(`/templates/${result.template.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Copy failed");
    } finally {
      setCopyBusy(false);
    }
  }

  if (loading || !detail || !id) {
    return (
      <article className="mx-auto min-h-[60vh] w-full max-w-2xl px-4 py-8 sm:py-10">
        <ErrorBanner message={error} />
        <p className="mb-6">
          <a
            href="/community"
            className="text-sm font-medium text-brand-700 no-underline hover:underline"
          >
            ← Community
          </a>
        </p>
        <p className="text-sm text-muted">Loading…</p>
      </article>
    );
  }

  const signupHref = `/register?next=${encodeURIComponent(`/community/templates/${detail.id}`)}`;
  const cta = isAnonymous ? (
    <AnchorButton href={signupHref} fullWidth>
      {signupLabel("start", signupMode)}
    </AnchorButton>
  ) : (
    <div className="grid grid-cols-2 gap-3">
      <Button
        type="button"
        fullWidth
        className={communityPairButtonClass}
        onClick={() => setStartOpen(true)}
      >
        Start a checklist
      </Button>
      <Button
        type="button"
        variant="secondary"
        fullWidth
        className={communityPairButtonClass}
        disabled={copyBusy}
        onClick={() => void onCopy()}
      >
        {copyBusy ? "Copying…" : "Copy to my templates"}
      </Button>
    </div>
  );

  return (
    <>
      <CommunityTemplateDetailView
        template={toDetail(detail)}
        cta={cta}
        banner={<ErrorBanner message={error} />}
      />
      {isAnonymous ? null : (
        <StartChecklistModal
          open={startOpen}
          sourceName={detail.name}
          onClose={() => setStartOpen(false)}
          onStart={async (name) => {
            const result = await startCommunityTemplate(detail.id, name);
            return result.checklist;
          }}
        />
      )}
    </>
  );
}
